import React from "react";

/* ------------------------------------------------------------------ */
/*  TRITAL Care® + TRITAL ShiftBridge™ master brand asset (Feb 2026)  */
/*                                                                    */
/*  Two locked brand systems:                                         */
/*  1. TRITAL Care® corporate (default platform="trital")             */
/*  2. TRITAL ShiftBridge™ PRN marketplace (platform="shiftbridge")   */
/*                                                                    */
/*  ShiftBridge sizing follows README_IMPLEMENTATION:                 */
/*  - Desktop header: 56px height (retina 112px source @2x)           */
/*  - Mobile header:  40px height (retina 80px source @2x)            */
/*  - Footer:         48px height (retina 96px source @2x)            */
/* ------------------------------------------------------------------ */

const MASTER_LOGO = "/tritalcare-header-logo.png";

/* Footer master — locked brand asset used ONLY in site + marketplace footers. */
export const FOOTER_MASTER_LOGO = "/tritalcare-footer-logo.png";

/* Header master — locked brand asset used for nav/header surfaces. */
export const HEADER_MASTER_LOGO = "/tritalcare-header-logo.png";

/* TRITAL ShiftBridge™ (PRN platform) — Professional logo assets per README spec */
/* Display sources (1×): used for srcset fallback */
export const SHIFTBRIDGE_HEADER_DISPLAY = "/images/logos/trital-shiftbridge-header-desktop-display-56px.png";
export const SHIFTBRIDGE_MOBILE_DISPLAY = "/images/logos/trital-shiftbridge-mobile-display-40px.png";
export const SHIFTBRIDGE_FOOTER_DISPLAY = "/images/logos/trital-shiftbridge-footer-display-48px.png";
/* Retina sources (2×): rendered at half-height for crisp HiDPI */
export const SHIFTBRIDGE_HEADER_RETINA  = "/images/logos/trital-shiftbridge-header-retina-112px.png";
export const SHIFTBRIDGE_MOBILE_RETINA  = "/images/logos/trital-shiftbridge-mobile-retina-80px.png";
export const SHIFTBRIDGE_FOOTER_RETINA  = "/images/logos/trital-shiftbridge-footer-retina-96px.png";
/* Master transparent (used for OG/social/SEO) */
export const SHIFTBRIDGE_MASTER         = "/images/logos/trital-shiftbridge-logo-transparent-master.png";
/* Favicon */
export const SHIFTBRIDGE_FAVICON        = "/images/logos/trital-shiftbridge-favicon-wide-32px.png";

// Legacy reference (kept for compatibility)
export const SHIFT_CONNECT_LOGO = SHIFTBRIDGE_HEADER_RETINA;

export default function TritalLogo({
  variant   = "light",
  className = "",
  iconOnly  = false,
  size      = "md",
  platform  = "trital",       // "trital" (default) | "shiftbridge"
  surface   = "header",       // "header" | "footer"
}) {
  /* ---------------- TRITAL ShiftBridge™ — PRN marketplace ---------------- */
  if (platform === "shiftbridge" || platform === "shift-connect") {
    if (surface === "footer") {
      return (
        <div className={`flex items-center ${className}`} data-testid="shiftbridge-footer-logo">
          <img
            src={SHIFTBRIDGE_FOOTER_RETINA}
            srcSet={`${SHIFTBRIDGE_FOOTER_DISPLAY} 1x, ${SHIFTBRIDGE_FOOTER_RETINA} 2x`}
            alt="TRITAL ShiftBridge™ — PRN Shift Marketplace"
            className="h-12 w-auto object-contain shrink-0 select-none"
            draggable={false}
            width={96}
            height={48}
          />
        </div>
      );
    }
    /* Header (default): responsive — 40px on mobile, 56px on desktop, retina-aware */
    return (
      <div className={`flex items-center ${className}`} data-testid="shiftbridge-logo">
        <picture>
          <source
            media="(min-width: 768px)"
            srcSet={`${SHIFTBRIDGE_HEADER_DISPLAY} 1x, ${SHIFTBRIDGE_HEADER_RETINA} 2x`}
          />
          <img
            src={SHIFTBRIDGE_MOBILE_DISPLAY}
            srcSet={`${SHIFTBRIDGE_MOBILE_DISPLAY} 1x, ${SHIFTBRIDGE_MOBILE_RETINA} 2x`}
            alt="TRITAL ShiftBridge™ — PRN Shift Marketplace"
            className="h-10 md:h-14 w-auto object-contain shrink-0 select-none"
            draggable={false}
          />
        </picture>
      </div>
    );
  }

  /* ---------------- Icon-only crop — tight circles in chrome ---------------- */
  if (iconOnly) {
    const d = size === "lg" ? "w-12 h-12" : size === "sm" ? "w-9 h-9" : "w-10 h-10";
    return (
      <div className={`flex items-center ${className}`} data-testid="trital-logo-icon">
        <img
          src={MASTER_LOGO}
          alt="TRITAL Care®"
          className={`${d} select-none object-cover object-left ring-1 ring-black/5 rounded-xl`}
          style={{ objectPosition: "left center" }}
          draggable={false}
        />
      </div>
    );
  }

  /* ---------------- Full TRITAL Care® horizontal lockup ---------------- */
  const h =
    size === "lg" ? "h-16" :
    size === "sm" ? "h-11" :
                    "h-14";

  const shadow =
    variant === "dark"
      ? "drop-shadow-[0_1px_6px_rgba(0,0,0,0.35)]"
      : "";

  return (
    <div className={`flex items-center ${className}`} data-testid="trital-logo">
      <img
        src={MASTER_LOGO}
        alt="TRITAL Care® — Triage Precision. Trusted Workforce."
        className={`${h} w-auto select-none object-contain ${shadow}`}
        draggable={false}
        width={1200}
        height={294}
      />
    </div>
  );
}
